// src/pages/EmailTemplates.js
import React, { useState, useEffect, useRef } from "react";

const PRIMARY = "#1F2937";

const TEMPLATES = [
  {
    key:         "user-welcome",
    label:       "New Learner Welcome",
    description: "Sent when an admin or supervisor invites a new external learner (individually or via bulk import) and chooses to send a welcome notification.",
    placeholders: [
      { tag:"{{name}}",        desc:"The learner's full name" },
      { tag:"{{email}}",       desc:"The learner's email address" },
      { tag:"{{courseList}}", desc:"HTML list of mandatory courses they need to complete (auto-generated)" },
      { tag:"{{courseCount}}",desc:"Number of mandatory courses" },
      { tag:"{{link}}",        desc:"Direct link to the training portal" },
    ],
  },
  {
    key:         "magic-link",
    label:       "Magic Link Login Email",
    description: "Sent when an external user requests a login link from the sign-in page.",
    placeholders: [
      { tag:"{{name}}",  desc:"The user's full name" },
      { tag:"{{link}}",  desc:"The one-time login link (required)" },
      { tag:"{{email}}", desc:"The user's email address" },
    ],
  },
  {
    key:         "course-assigned-mandatory",
    label:       "Course Assigned — Mandatory",
    description: "Sent when a user is individually assigned a course as mandatory training.",
    placeholders: [
      { tag:"{{name}}",       desc:"The user's full name" },
      { tag:"{{courseName}}", desc:"Name of the course" },
      { tag:"{{frequency}}",  desc:"Renewal frequency (e.g. 12 Months)" },
      { tag:"{{duration}}",   desc:"Estimated duration (e.g. 20 minutes)" },
      { tag:"{{category}}",   desc:"Course category / department" },
      { tag:"{{link}}",       desc:"Direct link to My Training" },
    ],
  },
  {
    key:         "course-assigned-optional",
    label:       "Course Assigned — Optional",
    description: "Sent when a user is individually assigned a course as optional training.",
    placeholders: [
      { tag:"{{name}}",       desc:"The user's full name" },
      { tag:"{{courseName}}", desc:"Name of the course" },
      { tag:"{{duration}}",   desc:"Estimated duration (e.g. 20 minutes)" },
      { tag:"{{category}}",   desc:"Course category / department" },
      { tag:"{{link}}",       desc:"Direct link to My Training" },
    ],
  },
  {
    key:         "course-live-mandatory",
    label:       "Course Published — Mandatory (All Trainees)",
    description: "Sent to all active trainees when a mandatory course is published and set to Available to All.",
    placeholders: [
      { tag:"{{name}}",       desc:"The user's full name" },
      { tag:"{{courseName}}", desc:"Name of the course" },
      { tag:"{{frequency}}",  desc:"Renewal frequency (e.g. 12 Months)" },
      { tag:"{{duration}}",   desc:"Estimated duration (e.g. 20 minutes)" },
      { tag:"{{category}}",   desc:"Course category / department" },
      { tag:"{{link}}",       desc:"Direct link to My Training" },
    ],
  },
  {
    key:         "course-live-optional",
    label:       "Course Published — Optional (All Trainees)",
    description: "Sent to all active trainees when an optional course is published and set to Available to All.",
    placeholders: [
      { tag:"{{name}}",       desc:"The user's full name" },
      { tag:"{{courseName}}", desc:"Name of the course" },
      { tag:"{{duration}}",   desc:"Estimated duration (e.g. 20 minutes)" },
      { tag:"{{category}}",   desc:"Course category / department" },
      { tag:"{{link}}",       desc:"Direct link to My Training" },
    ],
  },
];

function ToolbarButton({ title, onClick, icon }) {
  return (
    <button type="button" title={title} onClick={onClick} className="editor-toolbar-btn">
      {icon}
    </button>
  );
}

function HtmlEditor({ value, onChange }) {
  const [mode,     setMode]     = useState("visual");
  const editorRef  = useRef(null);
  const suppressRef= useRef(false);

  useEffect(() => {
    if (mode === "visual" && editorRef.current) {
      suppressRef.current = true;
      editorRef.current.innerHTML = value || "";
      suppressRef.current = false;
    }
  }, [mode]); // eslint-disable-line

  useEffect(() => {
    if (editorRef.current) editorRef.current.innerHTML = value || "";
  }, []); // eslint-disable-line

  function exec(cmd, arg) {
    editorRef.current?.focus();
    document.execCommand(cmd, false, arg);
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  }

  return (
    <div className="html-editor">
      <div className="editor-toolbar">
        <div className="editor-toolbar-group">
          <ToolbarButton title="Bold"      onClick={()=>exec("bold")}      icon={<strong style={{fontSize:13}}>B</strong>}/>
          <ToolbarButton title="Italic"    onClick={()=>exec("italic")}    icon={<em style={{fontSize:13}}>I</em>}/>
          <ToolbarButton title="Underline" onClick={()=>exec("underline")} icon={<u style={{fontSize:13}}>U</u>}/>
        </div>
        <div className="editor-toolbar-sep"/>
        <div className="editor-toolbar-group">
          <ToolbarButton title="Bullet list" onClick={()=>exec("insertUnorderedList")} icon={<svg viewBox="0 0 16 16" fill="currentColor" width="14"><path d="M2 4a1 1 0 100-2 1 1 0 000 2zm3-1.5a.5.5 0 000 1h9a.5.5 0 000-1H5zm0 4a.5.5 0 000 1h9a.5.5 0 000-1H5zM2 8a1 1 0 100-2 1 1 0 000 2zm-1 3.5a1 1 0 112 0 1 1 0 01-2 0zm4-.5a.5.5 0 000 1h9a.5.5 0 000-1H5z"/></svg>}/>
        </div>
        <div className="editor-toolbar-sep"/>
        <div className="editor-toolbar-group">
          <ToolbarButton title="Insert link" onClick={()=>{const u=window.prompt("URL:");if(u)exec("createLink",u);}} icon={<svg viewBox="0 0 20 20" fill="currentColor" width="14"><path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd"/></svg>}/>
        </div>
        <div style={{flex:1}}/>
        <div className="editor-mode-toggle">
          <button type="button" className={`mode-btn ${mode==="visual"?"active":""}`} onClick={()=>setMode("visual")}>Visual</button>
          <button type="button" className={`mode-btn ${mode==="source"?"active":""}`} onClick={()=>setMode("source")}>HTML</button>
        </div>
      </div>
      {mode==="visual"
        ? <div ref={editorRef} className="editor-content" contentEditable suppressContentEditableWarning
            onInput={()=>{ if(!suppressRef.current&&editorRef.current) onChange(editorRef.current.innerHTML); }}/>
        : <textarea className="editor-source" value={value} onChange={e=>onChange(e.target.value)} spellCheck={false} rows={14}/>
      }
    </div>
  );
}

export default function EmailTemplates() {
  const [activeKey, setActiveKey]  = useState(TEMPLATES[0].key);
  const [subject,   setSubject]    = useState("");
  const [body,      setBody]       = useState("");
  const [loading,   setLoading]    = useState(true);
  const [saving,    setSaving]     = useState(false);
  const [saved,     setSaved]      = useState(false);
  const [error,     setError]      = useState(null);
  const [preview,   setPreview]    = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [testSent,    setTestSent]    = useState(false);
  const [testError,   setTestError]   = useState(null);

  const tmpl = TEMPLATES.find(t => t.key === activeKey);

  useEffect(() => {
    setLoading(true); setSaved(false); setError(null); setTestSent(false); setTestError(null);
    fetch(`/api/email-templates/${activeKey}`)
      .then(r => r.json())
      .then(d => { setSubject(d.subject||""); setBody(d.body||""); })
      .finally(() => setLoading(false));
  }, [activeKey]);

  async function handleSave() {
    setSaving(true); setError(null); setSaved(false);
    try {
      const res = await fetch(`/api/email-templates/${activeKey}`, {
        method:"PUT", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ subject, body }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      setSaved(true); setTimeout(()=>setSaved(false),3000);
    } catch(e) { setError(e.message); }
    finally { setSaving(false); }
  }

  async function handleSendTest() {
    setTestSending(true); setTestError(null); setTestSent(false);
    try {
      const res = await fetch(`/api/email-templates/${activeKey}/test`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ subject, body }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      setTestSent(true); setTimeout(()=>setTestSent(false), 5000);
    } catch(e) { setTestError(e.message); }
    finally { setTestSending(false); }
  }

  // Sample preview with dummy values
  function previewHtml() {
    return body
      .replace(/\{\{name\}\}/g,  "Jane Smith")
      .replace(/\{\{link\}\}/g,  "#")
      .replace(/\{\{email\}\}/g, "jane@example.com");
  }

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">Email Templates</div><div className="page-subtitle">Edit system email templates</div></div>
      </div>

      {/* Template tabs */}
      {TEMPLATES.length > 1 && (
        <div className="card" style={{padding:0,marginBottom:16,overflow:"hidden"}}>
          <div className="builder-tabs">
            {TEMPLATES.map(t=>(
              <button key={t.key} className={`builder-tab ${activeKey===t.key?"active":""}`} onClick={()=>setActiveKey(t.key)}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div style={{display:"grid",gridTemplateColumns:"1fr 280px",gap:20,alignItems:"start"}}>
        {/* Editor */}
        <div className="card">
          {loading ? (
            <div className="page-loading"><span className="spinner" style={{width:24,height:24}}/></div>
          ) : (
            <>
              <p style={{fontSize:13,color:"var(--gray-500)",marginBottom:16,lineHeight:1.6}}>{tmpl?.description}</p>

              <div className="form-group">
                <label>Subject Line</label>
                <input value={subject} onChange={e=>setSubject(e.target.value)} placeholder="Email subject…"/>
              </div>

              <div className="form-group">
                <label>Email Body</label>
                <HtmlEditor value={body} onChange={setBody}/>
              </div>

              <div style={{display:"flex",alignItems:"center",gap:12,marginTop:8,flexWrap:"wrap"}}>
                <button className="btn-primary" style={{background:PRIMARY}} onClick={handleSave} disabled={saving}>
                  {saving?"Saving…":"Save Template"}
                </button>
                <button className="btn-secondary" onClick={handleSendTest} disabled={testSending}>
                  {testSending ? "Sending…" : "✉ Send Test Email"}
                </button>
                <button className="btn-secondary" onClick={()=>setPreview(p=>!p)}>
                  {preview?"Hide Preview":"Show Preview"}
                </button>
                {saved     && <span style={{fontSize:13,color:"var(--green)",display:"flex",alignItems:"center",gap:4}}><svg viewBox="0 0 20 20" fill="currentColor" width="14"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>Saved</span>}
                {testSent  && <span style={{fontSize:13,color:"var(--green)"}}>✉ Test email sent to your inbox</span>}
                {error     && <span style={{fontSize:13,color:"var(--red)"}}>{error}</span>}
                {testError && <span style={{fontSize:13,color:"var(--red)"}}>Test failed: {testError}</span>}
              </div>

              {preview && (
                <div style={{marginTop:20}}>
                  <div style={{fontSize:12,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",color:"var(--gray-400)",marginBottom:8}}>Preview (sample values)</div>
                  <div style={{border:"1px solid var(--gray-200)",borderRadius:"var(--radius)",overflow:"hidden"}}>
                    <div style={{padding:"10px 16px",background:"var(--gray-50)",borderBottom:"1px solid var(--gray-200)",fontSize:13}}>
                      <span style={{color:"var(--gray-500)"}}>Subject: </span>
                      <strong>{subject.replace(/\{\{name\}\}/g,"Jane Smith").replace(/\{\{email\}\}/g,"jane@example.com")}</strong>
                    </div>
                    <div style={{padding:20,fontSize:14,lineHeight:1.7,color:"#111827"}} dangerouslySetInnerHTML={{__html:previewHtml()}}/>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Placeholders panel */}
        <div className="card" style={{position:"sticky",top:24}}>
          <div style={{fontSize:13,fontWeight:700,marginBottom:12}}>Available Placeholders</div>
          <p style={{fontSize:12,color:"var(--gray-500)",marginBottom:14,lineHeight:1.5}}>Click a placeholder to copy it, then paste into the editor.</p>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {tmpl?.placeholders.map(p=>(
              <div key={p.tag} style={{border:"1px solid var(--gray-200)",borderRadius:"var(--radius)",padding:"8px 10px",cursor:"pointer",transition:"border-color 0.12s,background 0.12s"}}
                onClick={()=>navigator.clipboard.writeText(p.tag)}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=PRIMARY;e.currentTarget.style.background="#F0F4F8";}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor="var(--gray-200)";e.currentTarget.style.background="";}}>
                <code style={{fontSize:12,fontWeight:700,color:PRIMARY,display:"block",marginBottom:3}}>{p.tag}</code>
                <div style={{fontSize:12,color:"var(--gray-500)"}}>{p.desc}</div>
              </div>
            ))}
          </div>
          <p style={{fontSize:11,color:"var(--gray-400)",marginTop:12,lineHeight:1.5}}>
            Placeholders are replaced with real values when the email is sent.
          </p>
        </div>
      </div>
    </div>
  );
}
